import type { ReactNode } from 'react'
import { useHasPermission } from '@/hooks/useHasPermission'
import { UnauthorizedModule } from '@/features/auth/UnauthorizedModule'

interface ModuleGateProps {
  permission: string
  moduleName: string
  children: ReactNode
}

/**
 * Route-level workspace gate. Generalizes the original `FinanceGate` from PR #174
 * to any module-access permission.
 *
 * Per RBAC Module spec #10 §3.2.
 *
 * Renders [children] when the user holds [permission], otherwise renders
 * `<UnauthorizedModule moduleName="..." />`.
 *
 * Example:
 * ```
 * <Route path="/finance/*" element={
 *   <ModuleGate permission={Permissions.FINANCE_ACCESS} moduleName="Finance">
 *     <FinanceRoutes />
 *   </ModuleGate>
 * } />
 * ```
 */
export function ModuleGate({ permission, moduleName, children }: ModuleGateProps) {
  const allowed = useHasPermission(permission)
  if (!allowed) return <UnauthorizedModule moduleName={moduleName} />
  return <>{children}</>
}
